import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Plus, ArrowLeftRight, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_VARIANT = {
  REQUESTED: 'default',
  PENDING_APPROVAL: 'default',
  ACCEPTED: 'success',
  REJECTED: 'destructive',
  CANCELLED: 'secondary',
};

export default function MySwapRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ shift_id: '', reason: '' });

  const load = useCallback(async () => {
    try {
      const [swapRes, shiftsRes] = await Promise.all([
        api.get('/roster/swap-requests', { params: { my_requests: true } }),
        // Same upcoming feed as My Roster (soonest first); without it the API returned past shifts, newest-created first.
        api.get('/roster/my-shifts', { params: { upcoming: true, page: 1, pageSize: 100 } }),
      ]);
      setRequests(swapRes.data.data || []);
      setMyShifts(shiftsRes.data.items || shiftsRes.data.data || []);
    } catch (e) {
      toast.error('Failed to load swap requests');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh on focus
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', load);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  // Only shifts that have not started yet ('upcoming' also returns yesterday's, for an overnight shift still running).
  const pad = (n) => String(n).padStart(2, '0');
  const d = new Date();
  const nowKey = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const eligibleShifts = myShifts.filter((s) => (
    s.status !== 'COMPLETED' && s.status !== 'CANCELLED' && s.status !== 'IN_PROGRESS' && !s.swap_requested
    && `${s.date}T${s.start_time || '00:00'}` > nowKey
  ));

  const submit = async () => {
    if (!form.shift_id) return toast.error('Choose a shift');
    if (!form.reason.trim()) return toast.error('A reason is required');
    setCreating(true);
    try {
      await api.post('/roster/swap-request', { shift_id: form.shift_id, reason: form.reason });
      toast.success('Swap request submitted');
      setDialogOpen(false);
      setForm({ shift_id: '', reason: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit swap');
    } finally {
      setCreating(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.post(`/roster/swap-request/${id}/cancel`);
      toast.success('Swap request cancelled');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-swap-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Shift Swaps</h1>
          <p className="text-muted-foreground mt-1">Request to swap a shift you can&apos;t work</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { setRefreshing(true); load(); }} disabled={refreshing} data-testid="my-swap-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="my-swap-request-new">
                <Plus className="h-4 w-4 mr-2" />Request Swap
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="my-swap-dialog">
              <DialogHeader>
                <DialogTitle>Request a Shift Swap</DialogTitle>
                <DialogDescription>A coordinator will review your request.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="swap-shift">Shift</Label>
                  <Select value={form.shift_id} onValueChange={(v) => setForm({ ...form, shift_id: v })}>
                    <SelectTrigger id="swap-shift" data-testid="my-swap-shift-select"><SelectValue placeholder="Pick a shift" /></SelectTrigger>
                    <SelectContent>
                      {eligibleShifts.length === 0
                        ? <SelectItem value="__none__" disabled>No eligible shifts</SelectItem>
                        : eligibleShifts.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.date} · {s.start_time}-{s.end_time} · {s.participant_name || 'Shift'}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="swap-reason">Reason</Label>
                  <Textarea id="swap-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why do you need to swap this shift?" data-testid="my-swap-reason" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={creating} data-testid="my-swap-submit">
                  {creating ? 'Submitting…' : 'Submit'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">Loading…</CardContent></Card>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ArrowLeftRight className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">You have no swap requests</p>
            <p className="text-xs text-muted-foreground mt-1">Tap &quot;Request Swap&quot; above to submit one.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => {
            const s = r.shift || {};
            const canCancel = r.status === 'REQUESTED' || r.status === 'PENDING_APPROVAL';
            return (
              <Card key={r.id} data-testid={`my-swap-row-${r.id}`}>
                <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
                  <div>
                    <CardTitle className="text-base">
                      {s.date || '—'} · {s.start_time || '—'}-{s.end_time || '—'}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      {s.participant_name || 'Shift'} {s.service_name ? `· ${s.service_name}` : ''}
                    </p>
                  </div>
                  <Badge variant={STATUS_VARIANT[r.status] || 'default'} data-testid={`my-swap-status-${r.id}`}>
                    {r.status}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-sm"><span className="text-muted-foreground">Reason: </span>{r.reason}</p>
                  {r.review_notes && (
                    <p className="text-sm mt-1"><span className="text-muted-foreground">Manager note: </span>{r.review_notes}</p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-muted-foreground">Submitted {new Date(r.created_at).toLocaleString()}</p>
                    {canCancel && (
                      <Button size="sm" variant="outline" onClick={() => cancel(r.id)} data-testid={`my-swap-cancel-${r.id}`}>
                        <XCircle className="h-4 w-4 mr-1.5" />Cancel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
